/**
 * Onboarding tour steps definition
 * ≤7 steps covering the core Editor flow
 */

export interface TourStep {
  id: string;
  target: string; // CSS selector or data-lumi attribute selector
  placement: 'top' | 'bottom' | 'left' | 'right' | 'center' | 'auto';
  content: {
    title?: string;
    body: string;
    primaryCTA?: string;
    secondaryCTA?: string;
  };
  spotlightPadding?: number;
  disableBeacon?: boolean;
  disableOverlayClose?: boolean;
  disableScrolling?: boolean;
  scrollOffset?: number;
}

export const onboardingSteps: TourStep[] = [
  {
    id: 'upload-data',
    target: '[data-lumi="btn-upload"]',
    placement: 'bottom',
    content: {
      title: 'Welcome to Lumiplot!',
      body: 'Upload a CSV to get going, or try our sample dataset. You can skip this tour and use your own data.',
    },
    spotlightPadding: 8,
    disableBeacon: true, // Show tooltip directly, no beacon click required
  },
  // Step 1: Add your data (index 0) - DONE
  // Step 2: Set roles (index 1) - roles-first flow
  {
    id: 'set-roles',
    target: '[data-lumi="role-x"]',
    placement: 'bottom',
    content: {
      title: 'Select data for the plot',
      body: 'Start by picking X and Y columns. For this sample, choose X = site, Y = value, Group by = treatment.',
    },
    spotlightPadding: 8,
    disableScrolling: true,
    scrollOffset: 0,
    disableBeacon: true, // Show tooltip directly, no beacon click required
  },
  {
    id: 'choose-chart-type',
    target: '[data-lumi="charttype-bar_vertical_grouped"]',
    placement: 'bottom',
    content: {
      title: 'Choose a chart type',
      body: 'Based on the sample data, Lumi suggested these plot types. We will use Grouped Bars chart for this example. Click on it to select it.',
    },
    spotlightPadding: 8,
    disableScrolling: true,
    scrollOffset: 0,
    disableBeacon: true, // Show tooltip directly, no beacon click required
  },
  {
    id: 'send-message',
    target: '[data-lumi="chat-send"]',
    placement: 'top',
    content: {
      title: 'Send your request',
      body: 'Now click the Send button to submit your chart request. Lumi will generate a plan for you to review.',
    },
    spotlightPadding: 8,
    disableScrolling: true,
    scrollOffset: 0,
    disableBeacon: true, // Show tooltip directly, no beacon click required
  },
  {
    id: 'review-plan',
    target: '[data-lumi="btn-plan-recommended"]',
    placement: 'left',
    content: {
      title: 'Review the plan',
      body: 'Preview Lumi\'s plan. If it looks right, click Approve & Run. Need changes? Choose Modify to adjust and replan.',
    },
    spotlightPadding: 8,
    disableBeacon: true, // Show tooltip directly, no beacon click required
  },
  {
    id: 'save-and-actions',
    target: '[data-lumi="btn-save"]',
    placement: 'bottom',
    content: {
      title: 'Save & share',
      body: 'Nice! You just made your first plot. Save, Download, Share, or Add to Multi-panel for publication. We won\'t navigate away. Just pick what you need. Continue editing in the Plot Editor on the left.',
      primaryCTA: 'Got it',
      secondaryCTA: 'Close',
    },
    spotlightPadding: 8,
    disableBeacon: true, // Show tooltip directly, no beacon click required
  },
];

/**
 * Sub-steps for step 7 (post-save CTAs)
 * These are shown sequentially after save completes
 * Currently disabled - array is empty
 */
export const postSaveCTASteps: TourStep[] = [];

